import { Alert, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AppAutocomplete, AppTextField } from "../form";
import { AppFormField, AppFormGrid } from "../layout";
import { GRID_FULL, GRID_HALF } from "../../lib/ui/formLayout";

export type WidgetRowBinding = {
  categoryField?: string;
  valueField?: string;
  seriesField?: string;
};

type FieldOption = { value: string; label: string };

type Props = {
  binding: WidgetRowBinding;
  fieldOptions: string[];
  onChange: (next: WidgetRowBinding) => void;
  showMissingWarning?: boolean;
  disabled?: boolean;
};

function normalizeBinding(binding: WidgetRowBinding): WidgetRowBinding {
  const next: WidgetRowBinding = {};
  if (binding.categoryField?.trim()) next.categoryField = binding.categoryField.trim();
  if (binding.valueField?.trim()) next.valueField = binding.valueField.trim();
  if (binding.seriesField?.trim()) next.seriesField = binding.seriesField.trim();
  return next;
}

export default function WidgetRowBindingEditor({
  binding,
  fieldOptions,
  onChange,
  showMissingWarning = false,
  disabled,
}: Props) {
  const { t } = useTranslation();

  const options: FieldOption[] = useMemo(() => {
    const values = new Set<string>();
    for (const field of fieldOptions) {
      if (field?.trim()) values.add(field.trim());
    }
    for (const key of [binding.categoryField, binding.valueField, binding.seriesField]) {
      if (key?.trim()) values.add(key.trim());
    }
    return Array.from(values)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
  }, [fieldOptions, binding.categoryField, binding.valueField, binding.seriesField]);

  const pick = (key: keyof WidgetRowBinding, option: FieldOption | string | null) => {
    const value = typeof option === "string" ? option : option?.value ?? "";
    onChange(normalizeBinding({ ...binding, [key]: value }));
  };

  const freeSoloField = (
    label: string,
    key: keyof WidgetRowBinding,
    helperText?: string
  ) => (
    <AppAutocomplete<FieldOption, false, false, true>
      label={label}
      options={options}
      value={binding[key] ? { value: binding[key]!, label: binding[key]! } : null}
      onChange={(_, option) => pick(key, option)}
      onInputChange={(_, value, reason) => {
        if (reason === "input") pick(key, value);
      }}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      freeSolo
      disabled={disabled}
      helperText={helperText}
    />
  );

  return (
    <Stack spacing={1.25}>
      <Typography variant="subtitle2">{t("widget.rowBindingTitle")}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t("widget.rowBindingHint")}
      </Typography>
      {showMissingWarning ? (
        <Alert severity="warning">{t("widget.rowBindingMissing")}</Alert>
      ) : null}
      <AppFormGrid spacing="compact">
        <AppFormField size={GRID_HALF}>
          {options.length > 0 ? (
            freeSoloField(t("widget.rowBindingCategory"), "categoryField")
          ) : (
            <AppTextField
              label={t("widget.rowBindingCategory")}
              value={binding.categoryField ?? ""}
              onChange={(e) => pick("categoryField", e.target.value)}
              disabled={disabled}
            />
          )}
        </AppFormField>
        <AppFormField size={GRID_HALF}>
          {options.length > 0 ? (
            freeSoloField(t("widget.rowBindingValue"), "valueField")
          ) : (
            <AppTextField
              label={t("widget.rowBindingValue")}
              value={binding.valueField ?? ""}
              onChange={(e) => pick("valueField", e.target.value)}
              disabled={disabled}
            />
          )}
        </AppFormField>
        <AppFormField size={GRID_FULL}>
          {options.length > 0 ? (
            freeSoloField(t("widget.rowBindingSeries"), "seriesField")
          ) : (
            <AppTextField
              label={t("widget.rowBindingSeries")}
              value={binding.seriesField ?? ""}
              onChange={(e) => pick("seriesField", e.target.value)}
              disabled={disabled}
            />
          )}
        </AppFormField>
      </AppFormGrid>
    </Stack>
  );
}
